import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from './ImageUpload';
import { useCategories } from '../context/CategoriesContext';

const isInternal = (href) => href && href.startsWith('/');

/**
 * FeaturedTicker — continuous horizontal marquee of slides.
 * Stretched full-width, fixed-height. Admin controls:
 *  - speed (pixels per second)
 *  - direction ('left' = right-to-left, 'right' = left-to-right)
 *  - height (px)
 *  - gap between items (px)
 *  - margin top/bottom (px) — white spacer above and below the band
 */
const FeaturedTicker = ({
  slides = [],
  speed = 80,            // pixels / second
  direction = 'left',    // 'left' | 'right'
  height = 180,          // px
  gap = 16,              // px gap between items
  itemWidth = 320,       // px width per slide card
  marginTop = 24,        // px above the ticker
  marginBottom = 24,     // px below the ticker
  background = '#ffffff',// hex/rgb — page background color (used for edge fades)
}) => {
  const { categories } = useCategories();

  const resolved = useMemo(
    () =>
      slides.map((s, i) => {
        const linkedCategory = s.link?.startsWith('/category/')
          ? categories.find((c) => c.slug === s.link.replace('/category/', ''))
          : null;
        return {
          ...s,
          _idx: i,
          _image:
            resolveImageUrl(s.image) ||
            resolveImageUrl(linkedCategory?.image) ||
            resolveImageUrl(categories[categories.length - 1]?.image),
          _bg: s.bg || linkedCategory?.bg || '#E0F2FE',
          _color: s.color || linkedCategory?.color || '#0EA5E9',
          _text_color: s.text_color || '#FFFFFF',
        };
      }),
    [slides, categories],
  );

  if (resolved.length === 0) return null;

  // Loop content twice for a seamless marquee
  const loop = [...resolved, ...resolved];

  // Total width of one track and the animation duration so motion is constant
  const trackWidth = resolved.length * (itemWidth + gap);
  const safeSpeed = Math.max(10, Number(speed) || 80);
  const durationSec = trackWidth / safeSpeed;
  const dir = direction === 'right' ? 'reverse' : 'normal';
  const animName = 'mr-ticker-scroll';

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        height: `${height}px`,
        marginTop: `${marginTop}px`,
        marginBottom: `${marginBottom}px`,
        background,
      }}
      data-testid="featured-ticker"
      aria-label="Featured items ticker"
    >
      <div
        className="ticker-track flex items-stretch h-full"
        style={{
          gap: `${gap}px`,
          width: `${trackWidth * 2}px`,
          animation: `${animName} ${durationSec}s linear infinite ${dir}`,
        }}
      >
        {loop.map((r, i) => {
          const Wrap = isInternal(r.link) ? Link : r.link ? 'a' : 'div';
          const props = isInternal(r.link)
            ? { to: r.link }
            : r.link
            ? { href: r.link, target: r.link.startsWith('http') ? '_blank' : undefined, rel: 'noreferrer' }
            : {};
          return (
            <Wrap
              key={`${r._idx}-${i}`}
              {...props}
              className="relative flex-shrink-0 rounded-2xl overflow-hidden group block"
              style={{
                width: `${itemWidth}px`,
                background: r._bg,
              }}
              data-testid={`ticker-slide-${r._idx}`}
            >
              {r._image && (
                <img
                  src={r._image}
                  alt={r.title || ''}
                  className="absolute inset-0 w-full h-full object-contain p-3"
                  draggable="false"
                  loading="lazy"
                />
              )}
              {(r.title || r.subtitle) && (
                <div
                  className="absolute inset-x-0 bottom-0 p-3"
                  style={{ color: r._text_color }}
                >
                  {r.eyebrow && (
                    <div
                      className="text-[10px] font-bold tracking-[0.2em] uppercase"
                      style={{ color: r._color }}
                    >
                      {r.eyebrow}
                    </div>
                  )}
                  {r.title && (
                    <div
                      className="font-display text-sm font-bold leading-tight"
                      style={{ color: r._text_color }}
                    >
                      {r.title}
                    </div>
                  )}
                  {r.subtitle && (
                    <div
                      className="text-[11px] mt-0.5 opacity-80"
                      style={{ color: r._text_color }}
                    >
                      {r.subtitle}
                    </div>
                  )}
                </div>
              )}
            </Wrap>
          );
        })}
      </div>

      <style>{`
        @keyframes ${animName} {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-${trackWidth}px, 0, 0); }
        }
        .ticker-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default FeaturedTicker;
